import { useEffect, useRef, useState } from 'react';
import { TimeSlotDetailDialog } from '@/components/time-slot-detail-dialog';
import type { LandingScheduleDay, TimeSlot } from '@/types';
import type { LandingVariant, LandingVariantProps } from './landing-variant';

const STYLES: Record<
  LandingVariant,
  {
    dayLabel: string;
    slotButton: string;
    slotTitle: string;
    dialogClass: string | undefined;
  }
> = {
  ph26: {
    dayLabel: 'font-medium font-sans text-background text-lg md:text-xl',
    slotButton:
      'group flex h-[4.5rem] w-full transform-gpu flex-col justify-center gap-1 px-4 text-left transition-[opacity,transform,background-color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform hover:bg-primary/10',
    slotTitle:
      'w-full truncate font-medium font-sans text-primary text-xs transition-transform duration-300 group-hover:translate-x-1 md:text-sm',
    dialogClass: 'theme-ph26 selection-on-light bg-background text-foreground',
  },
  ar: {
    dayLabel: 'font-bold font-title text-background text-lg md:text-xl',
    slotButton:
      'group flex w-full transform-gpu flex-col gap-1 px-4 py-3 text-left transition-[opacity,transform,background-color] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform hover:bg-primary/10',
    slotTitle:
      'font-bold font-title text-primary text-sm transition-transform duration-300 group-hover:translate-x-1 md:text-base',
    dialogClass: undefined,
  },
};

// The venue timezone and the locale the clock is printed in were `const TZ` /
// `'es-CO'` in the Bogotá copy of this file; here they are the edition's, handed down
// from LandingEditions (PORTING.md G5).
function formatTime(date: Date, timeZone: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

type Props = {
  days: LandingScheduleDay[];
  timeZone: string;
  locale: string;
} & LandingVariantProps;

export function ScheduleDays({ days, timeZone, locale, variant }: Props) {
  const styles = STYLES[variant];
  const gridRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
            return;
          }
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleSlotClick = (slot: TimeSlot) => {
    setSelectedSlot(slot);
    setIsDialogOpen(true);
  };

  let slotIndex = 0;

  return (
    <>
      <div ref={gridRef} className="mt-10 grid gap-6 md:grid-cols-3">
        {days.map((day, dayIndex) => (
          <div
            key={day.key}
            className={`flex transform-gpu flex-col border-2 border-primary bg-background transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] will-change-transform ${
              isVisible
                ? 'translate-y-0 opacity-100'
                : 'translate-y-6 opacity-0'
            }`}
            style={{ transitionDelay: `${dayIndex * 110}ms` }}
          >
            <div className="border-primary border-b-2 bg-primary px-4 py-3">
              <h3 className={styles.dayLabel}>{day.label}</h3>
            </div>
            <ul className="flex flex-col divide-y-2 divide-primary/15">
              {day.slots.map((slot) => {
                const delayMs = 240 + slotIndex * 35;
                slotIndex += 1;

                return (
                  <li key={slot.id}>
                    <button
                      type="button"
                      onClick={() => handleSlotClick(slot)}
                      className={`${styles.slotButton} ${
                        isVisible
                          ? 'translate-y-0 opacity-100'
                          : 'translate-y-3 opacity-0'
                      }`}
                      style={{ transitionDelay: `${delayMs}ms` }}
                    >
                      <span className="font-mono text-primary/70 text-xs">
                        {formatTime(new Date(slot.startTime), timeZone, locale)}–
                        {formatTime(new Date(slot.endTime), timeZone, locale)}
                      </span>
                      <p className={styles.slotTitle}>{slot.title}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <TimeSlotDetailDialog
        timeSlot={selectedSlot}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        timeZone={timeZone}
        className={styles.dialogClass}
      />
    </>
  );
}
